import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { Plus, Trash2, Star } from 'lucide-react';

/**
 * Item-level vendor sourcing (2026-02, refactored).
 * - Preferred vendor = single primary source (item-wide).
 * - Secondary vendors = unranked backups (item-wide).
 * SKU + price + lead time are managed PER VARIANT × VENDOR on the Variants tab —
 * this card only decides "who carries this item?"
 */
export function VendorSourcingCard({ item, vendors, activeVendors, canEdit, onChange }) {
  const links = item.vendor_links || [];
  const preferredId = item.preferred_vendor_id || '';

  const linkedIds = useMemo(() => new Set(links.map(l => l.vendor_id)), [links]);
  const addableVendors = useMemo(
    () => activeVendors.filter(v => !linkedIds.has(v.id)),
    [activeVendors, linkedIds]
  );
  const vendorName = (vid) => vendors.find(v => v.id === vid)?.name || 'Unknown vendor';

  const setPreferred = (vid) => {
    const nextLinks = [...links];
    if (vid && !linkedIds.has(vid)) {
      nextLinks.push({ vendor_id: vid, vendor_sku: null, price: null, lead_time_days: null, last_purchased_at: null });
    }
    onChange({ ...item, preferred_vendor_id: vid || '', vendor_links: nextLinks });
  };

  const addLink = (vid) => {
    if (!vid || linkedIds.has(vid)) return;
    onChange({
      ...item,
      vendor_links: [...links, { vendor_id: vid, vendor_sku: null, price: null, lead_time_days: null, last_purchased_at: null }],
    });
  };

  const removeLink = (vendorId) => {
    const nextLinks = links.filter(l => l.vendor_id !== vendorId);
    const nextPreferred = vendorId === preferredId ? '' : preferredId;
    onChange({ ...item, preferred_vendor_id: nextPreferred, vendor_links: nextLinks });
  };

  const makePreferred = (vid) => setPreferred(vid);

  return (
    <Card data-testid="vendor-sourcing-card">
      <CardHeader>
        <CardTitle>Vendors</CardTitle>
        <CardDescription>
          Pick which vendors carry this item. Set a preferred vendor to make it the default
          source on POs. Per-size <b>vendor SKU and price</b> live on the Variants tab — a
          Small can be priced differently from a 2XL for the same vendor.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label>Preferred Vendor</Label>
          <Select
            value={preferredId || '_none'}
            onValueChange={(v) => setPreferred(v === '_none' ? '' : v)}
            disabled={!canEdit}
          >
            <SelectTrigger data-testid="item-preferred-vendor-select">
              <SelectValue placeholder="No preferred vendor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— No preferred vendor —</SelectItem>
              {activeVendors.map(v => (
                <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Linked Vendors ({links.length})</Label>
          {links.length === 0 ? (
            <div className="text-sm text-slate-500 italic py-3 text-center border border-dashed rounded-md">
              No vendors linked yet. Add one below or set a preferred vendor above.
            </div>
          ) : (
            <div className="border rounded-md divide-y">
              {links.map((link) => {
                const isPreferred = link.vendor_id === preferredId;
                return (
                  <div
                    key={link.vendor_id}
                    className="p-3 flex items-center justify-between gap-3"
                    data-testid={`vendor-link-row-${link.vendor_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{vendorName(link.vendor_id)}</span>
                      {isPreferred && (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1">
                          <Star className="w-3 h-3" /> Preferred
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {!isPreferred && canEdit && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => makePreferred(link.vendor_id)}
                          className="h-8 px-2 text-xs"
                          title="Mark as preferred"
                          data-testid={`vendor-link-make-preferred-${link.vendor_id}`}
                        >
                          <Star className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLink(link.vendor_id)}
                          className="h-8 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Remove vendor"
                          data-testid={`vendor-link-remove-${link.vendor_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {canEdit && (
          <div className="flex items-end gap-2 pt-2 border-t">
            <div className="flex-1">
              <Label className="text-xs text-slate-600">Add Secondary Vendor</Label>
              <Select
                key={links.length}
                value=""
                onValueChange={(v) => addLink(v)}
                disabled={addableVendors.length === 0}
              >
                <SelectTrigger data-testid="add-vendor-link-select">
                  <SelectValue placeholder={addableVendors.length === 0 ? 'All vendors already linked' : 'Pick a vendor to add…'} />
                </SelectTrigger>
                <SelectContent>
                  {addableVendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-500 pb-2">
              <Plus className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
              Per-size SKU and price are set on the Variants tab.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
